import React from "react";
import { motion, useReducedMotion } from "framer-motion";

interface HowItWorksSectionProps {
  title: string;
  steps: string[];
  image: {
    src: string;
    alt: string;
  };
  className?: string;
}

export function HowItWorksSection({ 
  title, 
  steps, 
  image, 
  className = "" 
}: HowItWorksSectionProps) {
  const reduce = useReducedMotion();

  const stepColors = [
    'bg-red-500',
    'bg-orange-500', 
    'bg-yellow-500',
    'bg-green-500',
    'bg-blue-500',
    'bg-purple-500'
  ];

  return (
    <section className={`py-16 bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50 dark:from-slate-950/20 dark:via-gray-950/20 dark:to-blue-950/20 ${className}`}>
      <div className="container mx-auto px-[10%]">
        {/* Centered Title */}
        <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-foreground">
          {title}
        </h2>
        
        <div className="grid lg:grid-cols-[1fr_2fr] gap-12 items-stretch">
          {/* Left Column - Steps */}
          <div className="flex flex-col justify-center">
            <div className="space-y-8">
              {steps.map((step, idx) => (
                <motion.div
                  key={idx}
                  initial={reduce ? undefined : { opacity: 0, x: 20 }}
                  whileInView={reduce ? undefined : { opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: idx * 0.1 }}
                  className="flex items-start gap-4 relative justify-end text-right"
                >
                  {/* Connecting Line */}
                  {idx < steps.length - 1 && (
                    <div 
                      className="absolute right-5 top-12 w-0.5 h-8 bg-border"
                      style={{ top: '48px' }}
                    />
                  )}
                  {/* Step Text */}
                  <div className="flex-1 pt-1">
                    <div className="text-base md:text-lg font-medium text-foreground">
                      {step}
                    </div>
                  </div>
                  {/* Step Circle */}
                  <div 
                    className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold text-white shadow-lg relative z-10 ${
                      stepColors[idx % stepColors.length]
                    }`}
                  >
                    {idx + 1}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Right Column - Image */}
          <motion.div
            initial={reduce ? undefined : { opacity: 0, scale: 0.95 }}
            whileInView={reduce ? undefined : { opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="relative h-full min-h-[400px]"
          >
            <div className="rounded-2xl overflow-hidden shadow-2xl bg-background h-full">
              <img
                src={image.src}
                alt={image.alt}
                className="w-full h-full object-cover object-center"
                width={1005}
                height={800}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}